/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Varsler: the janazah composer and what was sent.  CA-28, CA-34, CA-37, CA-90.
 *
 * The one form in the product that announces a death to every phone. What it
 * must never do again:
 *  - carry a prefilled name (fault 54: the mockup's dead man, live);
 *  - walk its steps to "Sendt" while nothing was sent;
 *  - skip step 2 (fault 114): the name is typed a SECOND time, by a person
 *    reading it — never prefilled, never copied, autocomplete and spellcheck
 *    off, and ABOVE the button, because an input below its own action is not
 *    one anybody fills in first. Changing the name voids the confirmation.
 *    The server refuses without it; this is the panel doing its half.
 *  - state an audience it did not fetch ("1 284 appinstallasjoner" was typed).
 *  - show an approval button without the consequence beside it (fault 115);
 *  - publish on ONE click (QA A12, 2026-10-16): «Godkjenn og publiser» is two clicks whose second names what it does,
 *    and both buttons are disabled while their request runs;
 *  - turn the prayer time into an instant in the BROWSER's zone (QA A12): the time is sent as typed, and the server
 *    reads it on the mosque's wall (Europe/Oslo) and words «Når» from it when the field is empty.
 *
 * Dødsannonse by the family (B3, 2026-10-24; capability janaza_family_form): «Lag familielenke» makes a link the family
 * fills in on a phone (72 hours, used once; its address is shown ONCE), open links can be revoked with two clicks, the
 * family's draft is tagged «Fra familien» and shows their text to the approver, and a published janazah has «Last ned
 * plakat (PDF)». The family's contact (name, phone) is shown here only — never in the notice.
 */
import React, { useEffect, useState } from 'react';
import { call, can as canDo, download, errFields, errText, patch, post, send, type Body } from '../lib/api';
import { useLoad, useMsg } from '../lib/hooks';
import { useDirty, useNav } from '../lib/nav';
import { followersText, osloTime, osloWallNow, osloWallToIso } from '../lib/format';
import { Body as CardBody, Btn, Card, CopyField, Field, LoadError, Loading, Msg, Tag, TwoStep, type TagTone } from '../components/ui';
import { TargetPicker } from '../components/TargetPicker';
import { FormDialog } from '../components/dialog';
import { SharePanel } from '../components/SharePanel';
import { Social } from '../core';
import { shows as sharesSocial } from '../lib/social';
import { SeenCount, useSeenCounts } from '../components/SeenCount';

const NOTICE_TAG: Record<string, [TagTone, string]> = { janazah: ['red', 'Janazah'], urgent: ['red', 'Haster'], normal: ['grey', 'Vanlig'], push: ['grey', 'Varsel'] };
const STEPS = ['1 · Skriv', '2 · Bekreft', '3 · Godkjenn', '4 · Sendt'];
/** The API's field names → the labels on this form. */
const JZ_FIELDS: Record<string, string> = { deceased_name: 'Avdødes navn', deceasedName: 'Avdødes navn', prayer_at: 'Tidspunkt', prayerAt: 'Tidspunkt',
  prayer_when: 'Janazah-bønn', location_no: 'Sted', prayerPlace: 'Sted', burial_place: 'Gravferd', confirmName: 'Navnet skrevet en gang til' };
const FIELD_OF: Record<string, string> = { deceased_name: 'name', deceasedName: 'name', prayer_at: 'at', prayerAt: 'at', prayer_when: 'when', location_no: 'place', prayerPlace: 'place', burial_place: 'burial' };

export function Notices() {
  const { data, error, reload } = useLoad<Body>('/notices');
  const { refreshBadges } = useNav();
  const [msg, say] = useMsg();
  const after = async () => { await reload(); refreshBadges(); };
  // B3: the family's link and the poster, where the mosque has the capability and for whoever writes the janazah.
  const { caps } = useNav();
  const family = Boolean(caps && caps.includes(FAMILY_CAPABILITY)) && canDo('notices.write');
  return (
    <>
      {/* Owner 2026-10-23: both composers open in a popup from their «+ Ny …» button; the results stay on the page. */}
      <AnnouncementComposer onSent={after} />
      <Composer onSent={after} msg={msg} say={say} />
      {family && <FamilyLinks onChanged={after} />}
      {error ? <LoadError error={error} retry={reload} /> : !data ? <Loading /> : <>
        <ActiveAnnouncements data={data} onChanged={after} />
        <Sent data={data} onApproved={after} say={say} poster={family} />
      </>}
    </>
  );
}

/* ------------------------------------------------------------------ announcements (2026-10-21) */

/** The two places an announcement can be shown; what the API's `showInApp` / `showOnScreens` mean to a person. */
const WHERE_NO = {
  app: ['Appen', 'Kunngjøringer på Hjem i appen — og på nettsiden, som viser det samme.'],
  screens: ['Skjermene', 'Nyhetslinjen nederst på skjermene og kunngjøringsbildet i rotasjonen.'],
} as const;
const whereText = (n: { showInApp?: boolean; showOnScreens?: boolean }) =>
  [n.showInApp && 'appen', n.showOnScreens && 'skjermene'].filter(Boolean).join(' og ') || 'ingen steder';
const AN_FIELDS: Record<string, string> = { title_no: 'Tittel', body_no: 'Tekst', expiresAt: 'Vises til', showInApp: 'Vises i' };

/**
 * An ordinary announcement, written in the panel (owner, 2026-10-21: "which messages should appear" on the screens
 * and in the app, decided here). Until now the panel had only the janazah composer and every announcement came from
 * the phone, shown everywhere. Where it goes is chosen before it is published, and can be changed while it is up
 * (ActiveAnnouncements). Published at once — no approval: it is taken down with one click, unlike a janazah.
 */
function AnnouncementComposer({ onSent }: { onSent: () => void }) {
  const empty = { title: '', body: '', until: '', app: true, screens: true };
  const [f, setF] = useState(empty);
  const [bad, setBad] = useState<string[]>([]);
  const [msg, say] = useMsg();
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const typed = Boolean(f.title || f.body);
  useDirty('announcement', typed);
  const close = () => { setOpen(false); setF(empty); setBad([]); };
  const set = (k: keyof typeof empty) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const v = e.target instanceof HTMLInputElement && e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setF({ ...f, [k]: v }); setBad(bad.filter((x) => x !== k));
  };
  async function publish() {
    if (busy) return;
    const title = f.title.trim(), body = f.body.trim();
    const missing = [!title && 'title', !body && 'body', !f.app && !f.screens && 'where'].filter(Boolean) as string[];
    if (missing.length) { setBad(missing); return say(missing.includes('where') && missing.length === 1 ? 'Velg minst ett sted kunngjøringen skal vises.' : 'Skriv en tittel og en tekst først.', 'err'); }
    // The wall-clock value as typed, read on the Oslo wall (format.osloWallToIso) — never the office computer's zone.
    const expiresAt = f.until ? osloWallToIso(f.until) : undefined;
    if (f.until && !expiresAt) { setBad(['until']); return say('Tidspunktet kan ikke leses.', 'err'); }
    setBusy(true); say('');
    const r = await post('/notices', { kind: 'normal', title, body, expiresAt, showInApp: f.app, showOnScreens: f.screens }).finally(() => setBusy(false));
    if (!r.ok) { say(errText(r.body, AN_FIELDS), 'err'); setBad(errFields(r.body).map((k) => ({ title_no: 'title', body_no: 'body', expiresAt: 'until', showInApp: 'where' })[k] || k)); return; }
    say(`Publisert i ${whereText({ showInApp: f.app, showOnScreens: f.screens })}. Skjermene får den ved neste oppdatering (innen et halvt minutt); appen når den åpnes.`, 'ok');
    setF(empty); setBad([]); setOpen(false); onSent();
  }
  return (
    <>
      <div className="new-row"><Btn small={false} onClick={() => { say(''); setOpen(true); }}>+ Ny kunngjøring</Btn></div>
      {!open && <Msg text={msg.text} tone={msg.tone} />}
      <FormDialog open={open} title="Ny kunngjøring" dirty={typed} onClose={close}
        hint="En beskjed til menigheten: parkering, en flyttet time, en innsamling. Du velger om den vises i appen, på skjermene eller begge steder — og kan endre det mens den er oppe. Ingen telefon vekkes; et varsel sender du under Arrangementer eller som janazah.">
        <div className="row">
          <Field label="Tittel" required className="field half">{(id) =>
            <input id={id} aria-invalid={bad.includes('title') || undefined} value={f.title} onChange={set('title')} placeholder="F.eks. Parkering stengt lørdag" maxLength={120} />}</Field>
          <Field label="Vises til" className="field half" note="Tomt: til den tas ned. Skjermene og appen tar den ned selv når tiden er ute.">{(id, d) =>
            <input id={id} type="datetime-local" aria-describedby={d} aria-invalid={bad.includes('until') || undefined} min={osloWallNow()} value={f.until} onChange={set('until')} />}</Field>
        </div>
        <Field label="Tekst" required note="Maks 600 tegn — den leses på en skjerm i en korridor og på en telefon.">{(id, d) =>
          <textarea id={id} rows={3} aria-describedby={d} aria-invalid={bad.includes('body') || undefined} value={f.body} onChange={set('body')} maxLength={600} placeholder="Hva skjer, og hva skal folk gjøre?" />}</Field>
        <div className="field" role="group" aria-labelledby="an-where">
          <span id="an-where" style={{ display: 'block', fontFamily: 'var(--font-form)', fontSize: 13, marginBottom: 5 }}>Vises i</span>
          <div className="row">
            {(['app', 'screens'] as const).map((k) => (
              <label key={k} className="chk" title={WHERE_NO[k][1]}><input type="checkbox" checked={f[k]} onChange={set(k)} aria-invalid={bad.includes('where') || undefined} /> {WHERE_NO[k][0]}</label>))}
          </div>
          <span className="note">Appen: kunngjøringene på Hjem (og nettsiden). Skjermene: nyhetslinjen nederst og kunngjøringsbildet. Hvilke skjermer velger du under «Vises på» etter publisering.</span>
        </div>
        <div className="row"><Btn disabled={busy} onClick={publish}>{busy ? 'Publiserer …' : 'Publiser'}</Btn></div>
        <Msg text={msg.text} tone={msg.tone} />
    </FormDialog>
    </>
  );
}

/**
 * The announcements that are up right now, with their switches. A change reaches the screens on their next poll and
 * the app when it is opened; «Ta ned» archives (the row stays in the history below). A janazah and a live notice are
 * not here: they are takeovers, shown everywhere, and come down by themselves or under «Sendte varsler».
 */
function ActiveAnnouncements({ data, onChanged }: { data: Body; onChanged: () => void }) {
  const items: Body[] = (data.items || []).filter((n: Body) => n.kind === 'normal' && n.active);
  const [msg, say] = useMsg();
  const [busyId, setBusyId] = useState<string | null>(null);
  // «Del i sosiale medier» (2026-10-22): an announcement that is up, for somebody who writes them, where the mosque has it.
  const { caps } = useNav();
  const canShare = sharesSocial(caps, canDo);
  const [sharing, setSharing] = useState<string | null>(null);
  // «Sett av M» (spec 01 C): how many signed-in app users opened each one — only with «Visningstall» on.
  const seen = useSeenCounts('notice', items.map((n) => n.id));
  const change = async (n: Body, body: Body, done: string) => {
    if (busyId) return;
    setBusyId(n.id); say('');
    const r = await patch('/notices/' + encodeURIComponent(n.id), body).finally(() => setBusyId(null));
    if (!r.ok) return say(errText(r.body, AN_FIELDS), 'err');
    say(done + ' ' + (r.body.message_no || ''), 'ok');
    onChanged();
  };
  return (
    <Card title={'Kunngjøringer som vises nå · ' + items.length} hint="Slå av og på hvor hver kunngjøring vises, sett når den skal ned, eller ta den ned. Endringer når skjermene ved neste oppdatering og appen når den åpnes.">
      <Msg text={msg.text} tone={msg.tone} />
      {items.length ? items.map((n) => (
        <div key={n.id} className="screenrow" style={{ alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="nm">{n.title_no}</div>
            {n.body_no && <div className="sub" style={{ whiteSpace: 'pre-wrap' }}>{n.body_no}</div>}
            <div className="sub">Skrevet av {n.createdByName || '—'} · {osloTime(n.createdAt)}{n.expiresAt ? ' · vises til ' + osloTime(n.expiresAt) : ' · til den tas ned'}</div>
            <SeenCount counts={seen} id={n.id} label="Sett av" block />
            <div className="row" style={{ marginTop: 8, alignItems: 'center' }} role="group" aria-label={'Hvor «' + n.title_no + '» vises'}>
              <label className="chk" title={WHERE_NO.app[1]}><input type="checkbox" checked={Boolean(n.showInApp)} disabled={busyId === n.id}
                onChange={(e) => change(n, { showInApp: e.target.checked }, e.target.checked ? 'Vises i appen.' : 'Tatt ut av appen.')} /> {WHERE_NO.app[0]}</label>
              <label className="chk" title={WHERE_NO.screens[1]}><input type="checkbox" checked={Boolean(n.showOnScreens)} disabled={busyId === n.id}
                onChange={(e) => change(n, { showOnScreens: e.target.checked }, e.target.checked ? 'Vises på skjermene.' : 'Tatt av skjermene.')} /> {WHERE_NO.screens[0]}</label>
              <label className="chk">Vises til <input type="datetime-local" min={osloWallNow()} defaultValue={n.expiresAt ? osloTime(n.expiresAt).replace(' ', 'T') : ''}
                aria-label={'«' + n.title_no + '» vises til'} disabled={busyId === n.id}
                onBlur={(e) => {
                  const v = e.target.value;
                  const iso = v ? osloWallToIso(v) : null;
                  if (v && !iso) return say('Tidspunktet kan ikke leses.', 'err');
                  if ((iso || null) !== (n.expiresAt || null)) change(n, { expiresAt: iso }, iso ? 'Tas ned ' + osloTime(iso) + '.' : 'Vises til den tas ned.');
                }} /></label>
            </div>
            {n.showOnScreens && <TargetPicker kind="notice" id={n.id} />}
            {sharing === n.id && Social.shareable('notice', n) && <SharePanel title={n.title_no} onClose={() => setSharing(null)}
              source={{ kind: 'notice', id: n.id, title: n.title_no || '', body: n.body_no || '' }} />}
          </div>
          {canShare && Social.shareable('notice', n) && <Btn ghost aria-expanded={sharing === n.id} aria-label={'Del «' + n.title_no + '» i sosiale medier'}
            onClick={() => setSharing(sharing === n.id ? null : n.id)}>Del</Btn>}
          <TwoStep label="Ta ned" confirmLabel="Klikk igjen: tas ned fra appen og skjermene" disabled={busyId === n.id}
            ariaLabel={'Ta ned «' + n.title_no + '»'}
            onConfirm={async () => {
              const r = await send('/notices/' + encodeURIComponent(n.id), { method: 'DELETE' });
              say(r.ok ? 'Tatt ned. Skjermene får det ved neste oppdatering.' : errText(r.body), r.ok ? 'ok' : 'err');
              onChanged();
            }} />
        </div>
      )) : <div className="card-body">Ingen kunngjøringer vises nå. Skriv en over, eller fra appens personalområde.</div>}
    </Card>
  );
}

function Composer({ onSent, msg, say }: { onSent: () => void; msg: { text: string; tone: any }; say: (t: string, tone?: any) => void }) {
  const empty = { name: '', when: '', at: '', place: '', burial: '' };
  const [f, setF] = useState(empty);
  const [step, setStep] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const [retype, setRetype] = useState('');
  const [retypeBad, setRetypeBad] = useState(false);
  const [tv, setTv] = useState(false);
  const [aud, setAud] = useState<{ text: string; warn: boolean } | null>(null);
  const [bad, setBad] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const confirmRef = React.useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const typed = Boolean(f.name || f.when || f.place || f.burial);
  useDirty('janazah', typed);
  // Closing throws the draft away, and with it any half-done confirmation: the next one starts at step 1.
  const close = () => { setOpen(false); setF(empty); setBad([]); setConfirming(false); setRetype(''); setRetypeBad(false); setTv(false); setStep(0); };

  useEffect(() => {
    call('/push/audience?priority=high').then((a: Body) => setAud({
      // Followers at the platform (FOLLOWING.md): today's count, «færre enn 5» below five; nothing while following is off.
      text: 'Går til: ' + a.summary_no + (followersText(a.followers) ? ' + ' + followersText(a.followers) : '') + ' · ' + a.screens_no + ' · ' + a.website_no + '. Krever godkjenning av en annen administrator før utsending.' + (a.note_no ? ' ' + a.note_no : ''),
      warn: a.people === 0 || Boolean(a.screens && a.screens.down),
    })).catch(() => setAud({ text: 'Antall mottakere kunne ikke hentes. Krever godkjenning av en annen administrator før utsending.', warn: true }));
  }, []);

  const set = (k: keyof typeof empty) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setF({ ...f, [k]: e.target.value });
    setBad(bad.filter((x) => x !== k));
    // A confirmation of the OLD spelling is void: confirm "Khan", correct to "Kahn", send — no.
    if (k === 'name' && confirming) { setConfirming(false); setStep(0); setRetype(''); }
  };
  const name = f.name.trim();
  const prevBody = ['Janazah-bønn ' + (f.when.trim() || '…'), f.place.trim(), f.burial.trim() ? 'gravferd: ' + f.burial.trim() : ''].filter(Boolean).join(' · ');

  async function send() {
    if (!confirming) {
      if (!name) return say('Skriv avdødes navn først.', 'err');
      say(''); setConfirming(true); setStep(1); setRetype('');
      setTimeout(() => confirmRef.current?.focus(), 0);
      return;
    }
    if (busy) return;
    setBusy(true);
    // The wall-clock value as typed ('2026-10-25T13:30'): the server reads it in the mosque's zone.
    const r = await post('/notices', { kind: 'janazah', deceasedName: name, confirmName: retype,
      prayerWhen: f.when.trim() || undefined, prayerAt: f.at || undefined,
      prayerPlace: f.place.trim() || undefined, burialPlace: f.burial.trim() || undefined }).finally(() => setBusy(false));
    if (r.ok) {
      say(r.body.message_no || 'Sendt til godkjenning.', 'ok');
      setConfirming(false); setRetype(''); setF(empty); setTv(false);
      setStep(0);   // Sent to approval: the popup closes and the result (and «Sendte varsler») stays on the page.
      setOpen(false);
      onSent();
    } else {
      say(errText(r.body, JZ_FIELDS), 'err');
      setBad(errFields(r.body).map((k) => FIELD_OF[k]).filter(Boolean));
      // A mismatch keeps the person on step 2 with an EMPTY box: read the name again, do not edit what you typed.
      if (r.body && (r.body.code === 'retype_mismatch' || r.body.code === 'retype_required')) {
        setRetype(''); setRetypeBad(true); confirmRef.current?.focus();
      }
    }
  }

  return (
    <>
      <div className="new-row"><Btn danger small={false} onClick={() => { say(''); setStep(0); setOpen(true); }}>+ Ny janazah-kunngjøring</Btn></div>
      {!open && <Msg text={msg.text} tone={msg.tone} id="jz-msg" />}
      <FormDialog open={open} title="Janazah-kunngjøring" dirty={typed} onClose={close}
        hint="Den raskeste veien ut til hele menigheten. Går som høyprioritetsvarsel til appen og tar over alle skjermene i bygget.">
        <div className="steps" aria-label="Steg">{STEPS.map((s, i) => <span key={s} className={i <= step ? 'on' : ''} aria-current={i === step ? 'step' : undefined}>{s}</span>)}</div>
        <div className="warnbox"><b>Dette varselet vekker telefoner.</b> Det går til alle installasjoner, uansett tid på døgnet, og tar over skjermene til bønnen er over. Kontroller navn og klokkeslett før du sender.</div>
        <div className="row">
          <Field label="Avdødes navn" required className="field half">{(id) => <input id={id} aria-invalid={bad.includes('name') || undefined} value={f.name} onChange={set('name')} placeholder="Fullt navn på avdøde" autoComplete="off" />}</Field>
          <Field label="Janazah-bønn" className="field half">{(id) => <input id={id} aria-invalid={bad.includes('when') || undefined} value={f.when} onChange={set('when')} placeholder="Tomt: lages fra tidspunktet, f.eks. «I morgen kl. 13:30»" />}</Field>
          <Field label="Tidspunkt" required className="field half" note="Kunngjøringen tar ned seg selv etter bønnen.">{(id, d) => <input id={id} type="datetime-local" aria-describedby={d} aria-invalid={bad.includes('at') || undefined} value={f.at} onChange={set('at')} />}</Field>
          <Field label="Sted" required className="field half">{(id) => <input id={id} aria-invalid={bad.includes('place') || undefined} value={f.place} onChange={set('place')} placeholder="F.eks. Hovedsalen" />}</Field>
        </div>
        <Field label="Gravferd">{(id) => <input id={id} aria-invalid={bad.includes('burial') || undefined} value={f.burial} onChange={set('burial')} placeholder="F.eks. gravlunden kl. 15:00" />}</Field>
        <div className="preview">
          <div className="t">🕊️ Janazah — {name || '…'}</div>
          <div className="b">{prevBody}</div>
        </div>
        {confirming && (
          <Field label="Skriv avdødes navn en gang til" required style={{ marginTop: 14 }} noteId="jz-confirm-note"
                 note="Les navnet i feltet over og skriv det av. Dette vises på alle skjermer i moskeen, i appen og på nettsiden, og kan ikke trekkes tilbake.">
            {(id, d) => <input id={id} ref={confirmRef} value={retype} aria-describedby={d} aria-invalid={retypeBad || undefined}
                               autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false}
                               onChange={(e) => { setRetype(e.target.value); setRetypeBad(false); }} />}
          </Field>
        )}
        <div className="row" style={{ marginTop: 14 }}>
          <Btn danger disabled={busy} aria-busy={busy || undefined} onClick={send}>{busy ? 'Sender …' : confirming ? 'Bekreft og send til godkjenning' : 'Send til godkjenning'}</Btn>
          <Btn ghost aria-expanded={tv} aria-controls="jz-tv" onClick={() => setTv(!tv)}>{tv ? 'Skjul skjermvisningen' : 'Forhåndsvis på skjerm'}</Btn>
        </div>
        {tv && (
          <div id="jz-tv" className="jz-tv" aria-label="Slik ser skjermene ut under kunngjøringen">
            <div className="kick">Janazah</div><div className="inna">Inna lillahi wa inna ilayhi raji'un</div>
            <div className="nm">{name || '…'}</div>
            <div className="det">{[f.when.trim(), f.place.trim()].filter(Boolean).join(' · ') || '…'}</div>
            <div className="sub">{f.burial.trim() ? 'Gravlegging: ' + f.burial.trim() : ''}</div>
          </div>
        )}
        <Msg text={msg.text} tone={msg.tone} id="jz-msg" />
        <span className={'field note' + (aud && aud.warn ? ' warn' : '')} id="jz-audience" style={{ marginTop: 10, display: 'block' }}>{aud ? aud.text : 'Går til: … (hentes)'}</span>
    </FormDialog>
    </>
  );
}

function Sent({ data, onApproved, say, poster }: { data: Body; onApproved: () => void; say: (t: string, tone?: any) => void; poster: boolean }) {
  const items: Body[] = data.items || [];
  const fromFamily = (n: Body) => n.fromFamily ? <> <Tag tone="gold">Fra familien</Tag></> : null;
  // «Sendt til N · Åpnet av M» (spec 01 C): N from the push's own stats; M (tapped in the app) only with «Visningstall» on.
  const opened = useSeenCounts('push', items.filter((n) => n.push && n.push.sentAt).map((n) => n.push.id));
  return (
    <Card title="Sendte varsler">
      <table className="t">
        <thead><tr><th scope="col">Melding</th><th scope="col">Type</th><th scope="col">Skrevet av</th><th scope="col">Godkjent av</th><th scope="col">Sendt</th></tr></thead>
        <tbody>
          {items.length ? items.map((n) => {
            const j = n.janazah || {};
            if (n.awaitingApproval) {
              // What it says, and — only for somebody allowed to press it — the button (core rule 20).
              return (
                <tr key={n.id} className="over">
                  <td><b>Janazah — {j.deceasedName || ''}</b>{fromFamily(n)}<br /><span className="note">{[j.prayerWhen, j.prayerPlace, j.burialPlace].filter(Boolean).join(' · ')}</span>
                    {/* What the family wrote is what the approver publishes: shown in full, not as a title. */}
                    {n.fromFamily && n.body_no && <div className="note" style={{ whiteSpace: 'pre-wrap', marginTop: 6 }}>{n.body_no}</div>}
                    <TargetPicker kind="notice" id={n.id} /></td>
                  <td><Tag tone="red">Janazah</Tag></td><td>{n.createdByName || '—'}</td>
                  <td>{n.canApprove
                    ? <TwoStep ghost={false} label="Godkjenn og publiser" busyLabel="Publiserer …"
                        ariaLabel={`Godkjenn og publiser janazah for ${j.deceasedName || ''}`}
                        confirmLabel="Klikk igjen: vises på alle skjermer og sendes som hastevarsel til alle telefoner"
                        onConfirm={async () => {
                          const r = await post('/notices/' + encodeURIComponent(n.id) + '/approve');
                          say(r.ok ? (r.body.message_no || 'Publisert.') : errText(r.body), r.ok ? 'ok' : 'err');
                          onApproved();
                        }} />
                    : <Tag tone="gold">venter på en annen</Tag>}
                    {n.approval && <div className="note approve-what">{n.approval.wakes_no} {n.approval.reaches_no} <b>{n.approval.irreversible_no}</b></div>}</td>
                  <td><Tag tone="gold">ikke publisert</Tag></td>
                </tr>
              );
            }
            const [tone, label] = NOTICE_TAG[n.kind] || ['grey', n.kind];
            const p = n.push;
            const title = n.kind === 'janazah' && j.deceasedName ? 'Janazah — ' + j.deceasedName : n.title_no;
            const detail = n.kind === 'janazah' ? [j.prayerWhen, j.prayerPlace].filter(Boolean).join(' · ')
              : n.kind === 'normal' ? 'Vises i ' + whereText(n) + (n.status === 'archived' ? ' · tatt ned' : n.active ? '' : ' · utløpt') : '';
            return (
              <tr key={n.id}>
                <td>{title}{n.kind === 'janazah' && fromFamily(n)}{detail && <><br /><span className="note">{detail}</span></>}
                  {/* A push to a selection from the register (2026-10-31): whom it goes to, by size only. */}
                  {p && p.reach_no && <><br /><span className="note">{p.reach_no}</span></>}{!(n.kind === 'normal' && n.active) && n.kind !== 'push' && <TargetPicker kind="notice" id={n.id} />}
                  {poster && n.kind === 'janazah' && n.status === 'published' && <div style={{ marginTop: 6 }}><Btn ghost aria-label={'Last ned plakat (PDF) for ' + (j.deceasedName || 'janazah')}
                    onClick={async () => {
                      const r = await download(`/v1/admin/janaza/notices/${encodeURIComponent(n.id)}/poster.pdf`, 'janazah-plakat.pdf');
                      if (!r.ok) say(r.message || 'Plakaten kunne ikke lastes ned.', 'err');
                    }}>Last ned plakat (PDF)</Btn></div>}</td>
                <td><Tag tone={tone}>{label}</Tag></td>
                <td>{n.createdByName || '—'}</td>
                {/* The approver column is the point of this table: an empty cell on a sent janazah is said in words. */}
                <td>{!p ? <Tag tone="grey">ikke sendt</Tag> : p.approvedByName ? p.approvedByName : <Tag tone="red">ikke godkjent</Tag>}</td>
                <td>{p && p.sentAt ? osloTime(p.sentAt) : p ? <Tag tone="gold">venter</Tag> : '—'}
                  {p && p.sentAt && (p.people != null || opened) && <div className="sent-reach">
                    {p.people != null && <span className="note">Sendt til {Number(p.people).toLocaleString('nb-NO')}</span>}
                    {p.people != null && opened && p.id in opened && <span className="note"> · </span>}
                    <SeenCount counts={opened} id={p.id} label="Åpnet av" /></div>}
                  {/* A family's janazah published by its link's sender (2026-10-25): the phone alert waits for somebody else. */}
                  {p && p.canApprove && <div style={{ marginTop: 6 }}><TwoStep ghost={false} label="Godkjenn og send varsel" busyLabel="Sender …"
                    ariaLabel={`Godkjenn og send varselet om ${title}`}
                    confirmLabel={p.priority === 'high' ? 'Klikk igjen: sendes som hastevarsel til alle telefoner'
                      : `Klikk igjen: sendes som varsel${p.reach_no ? ' til ' + p.reach_no.toLowerCase() : ''}`}
                    onConfirm={async () => {
                      const a = await post('/push/' + encodeURIComponent(p.id) + '/approve');
                      const r = a.ok ? await post('/push/' + encodeURIComponent(p.id) + '/send') : a;
                      say(r.ok ? (r.body.message_no || r.body.message || 'Varselet er sendt.') : errText(r.body), r.ok ? 'ok' : 'err');
                      onApproved();
                    }} /></div>}</td>
              </tr>
            );
          }) : <tr><td colSpan={5}>Ingen varsler er sendt ennå.</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}

/* ------------------------------------------------------------------ the family's link (B3, 2026-10-24) */

export const FAMILY_CAPABILITY = 'janaza_family_form';
const LINK_STATE: Record<string, [TagTone, string]> = { open: ['green', 'åpen'], used: ['grey', 'brukt'], expired: ['grey', 'utløpt'] };

/**
 * «Lag familielenke»: a link the family opens on a phone without signing in, to fill in the death notice themselves.
 * The address exists only in the answer that made it (the server keeps its hash): it is shown once, to copy and send.
 * Open links are listed with «Trekk tilbake» (two clicks); used ones show the family's contact — for staff only.
 */
function FamilyLinks({ onChanged }: { onChanged: () => void }) {
  const { data, error, reload } = useLoad<Body>('/janaza/family-links');
  const [msg, say] = useMsg();
  const [made, setMade] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const make = async () => {
    if (busy) return;
    setBusy(true); say(''); setMade(null);
    const r = await post('/janaza/family-links', {}).finally(() => setBusy(false));
    if (!r.ok) return say(errText(r.body), 'err');
    setMade(r.body.url); say(r.body.message_no || 'Lenken er laget.', 'ok');
    reload();
  };
  const links: Body[] = (data && data.links) || [];
  const shown = links.filter((l) => l.state === 'open' || l.state === 'used');
  return (
    <Card title="Dødsannonse fra familien" hint="Familien fyller ut dødsannonsen selv, på mobilen, uten å logge inn. Det de sender blir et utkast som en annen ansatt enn deg godkjenner før noe vises. Lenken virker i 72 timer og kan brukes én gang.">
      <div className="row" style={{ alignItems: 'center' }}>
        <Btn disabled={busy} aria-busy={busy || undefined} onClick={make}>{busy ? 'Lager …' : 'Lag familielenke'}</Btn>
      </div>
      {made && <CopyField label="Lenken til familien — vises bare nå" value={made} />}
      <Msg text={msg.text} tone={msg.tone} />
      {error ? <LoadError error={error} retry={reload} /> : !data ? <Loading /> : shown.length ? (
        <table className="t">
          <thead><tr><th scope="col">Laget</th><th scope="col">Status</th><th scope="col">Kontaktperson (bare for ansatte)</th><th scope="col"></th></tr></thead>
          <tbody>{shown.map((l) => {
            const [tone, label] = LINK_STATE[l.state] || ['grey', l.state];
            return (
              <tr key={l.id}>
                <td>{osloTime(l.createdAt)}{l.createdByName ? ' · ' + l.createdByName : ''}<br /><span className="note">{l.state === 'open' ? 'virker til ' + osloTime(l.expiresAt) : l.usedAt ? 'sendt ' + osloTime(l.usedAt) : ''}</span></td>
                <td><Tag tone={tone}>{label}</Tag></td>
                <td>{[l.contactName, l.contactPhone].filter(Boolean).join(' · ') || '—'}</td>
                <td>{l.state === 'open' && <TwoStep label="Trekk tilbake" confirmLabel="Klikk igjen: lenken slutter å virke" ariaLabel={'Trekk tilbake familielenken laget ' + osloTime(l.createdAt)}
                  onConfirm={async () => {
                    const r = await post('/janaza/family-links/' + encodeURIComponent(l.id) + '/revoke');
                    say(r.ok ? (r.body.message_no || 'Trukket tilbake.') : errText(r.body), r.ok ? 'ok' : 'err');
                    reload(); onChanged();
                  }} />}</td>
              </tr>
            );
          })}</tbody>
        </table>
      ) : <div className="card-body">Ingen åpne familielenker.</div>}
    </Card>
  );
}
